import { useApplicantAPI } from "@certn/api";
import {
  GridItem,
  Text,
  Grid,
  Link,
  SelectFormikField,
  SelectItem,
  showToast,
  FileUploadFormikField,
  TextAreaFormikField,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { Field, Formik, FormikProps } from "formik";
import { useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { object, string } from "yup";

import { ActionListItemWithModal } from "./ActionListItemWithModal";
import { FormModal } from "./FormModal";
import { useFormSubmit } from "../../../hooks/api/useSubmit";
import { baseFileTypes } from "../../../utils/file-types";

export function DisputeReportResults() {
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<any>>(null);

  const { data: completeCases, isLoading } = useApplicantAPI(
    "/api/internal/applicant/case/complete/"
  );

  const submitForm = useFormSubmit("/api/internal/applicant/raise-dispute/", {
    onSuccess: () => {
      showToast(t("Request sent"), {
        type: "info",
      });
    },
    raiseError: true,
  });

  const schema = completeCases?.length
    ? object().shape({
        description: string().required(
          t<string>("Please describe the dispute.")
        ),
        case: string().required(t<string>("Please select a case.")),
      })
    : object().shape({
        description: string().required(
          t<string>("Please describe the dispute.")
        ),
      });

  const initialValues = completeCases?.length
    ? {
        description: "",
        supporting_documents: [],
        case: "",
      }
    : {
        description: "",
        supporting_documents: [],
      };

  return (
    <ActionListItemWithModal
      testID="help-and-privacy-dispute-report-button"
      title={t("Dispute report results")}
      description={t(
        "Dispute information on your report that you believe is inaccurate or incomplete."
      )}
      link={t("Dispute")}
    >
      {({ close }) => (
        <Formik
          initialValues={initialValues}
          onSubmit={async (values, helpers) => {
            try {
              const supportingDocuments = values.supporting_documents.map(
                (fileInfo: { content: string }) => fileInfo.content
              );
              await submitForm(
                { ...values, supporting_documents: supportingDocuments },
                helpers
              );
              close(); // once form submission successful, close modal
            } catch (e) {
              console.error('Error in "Dispute report results":', e);
              showToast(t("Something went wrong, try again"), {
                type: "error",
              });
            }
          }}
          innerRef={formRef}
          validationSchema={schema}
        >
          {({ handleSubmit }) => {
            return (
              <FormModal
                title={t("Dispute report results")}
                onSubmit={handleSubmit}
                onCancel={close}
                onClose={close}
                continueText={t("Submit")}
                secondaryText={t("Cancel")}
                variant="success"
              >
                <Grid form>
                  <GridItem className={css({ flexDirection: "row" })}>
                    <Text>
                      <Text style="global/body-primary">
                        <Trans>
                          Dispute information on your report that you believe is
                          inaccurate or incomplete.
                        </Trans>
                      </Text>
                    </Text>
                  </GridItem>

                  {Boolean(completeCases?.length) && (
                    <GridItem>
                      <SelectFormikField
                        name="case"
                        label={t("Select case")}
                        isRequired
                        testID="case-select-field"
                        isLoading={isLoading}
                      >
                        {completeCases?.map((option) => (
                          <SelectItem
                            key={option.short_id}
                            id={option.short_id}
                            textValue={option.short_id}
                          >
                            {option.short_id}
                          </SelectItem>
                        ))}
                      </SelectFormikField>
                    </GridItem>
                  )}

                  <GridItem>
                    <Field
                      as={TextAreaFormikField}
                      label={t("Describe the dispute")}
                      name="description"
                      isRequired
                      description={t("Please be as specific as possible")}
                      testID="dispute-report-modal-describe-dispute-input"
                    />
                  </GridItem>

                  <GridItem>
                    <FileUploadFormikField
                      name="supporting_documents"
                      testID="dispute-report-modal-document-upload-button"
                      requestUrl="api/internal/applicant/generate-presigned-url/"
                      label={t("Supporting documents")}
                      fileTypes={baseFileTypes}
                      maxSizeBytes={10000000}
                      bucketDirectory="dispute_report_attachments"
                      multiple
                    />
                  </GridItem>

                  <GridItem>
                    <Text color="text-weak">
                      <p>
                        <Trans>
                          Certn will inform the requestor about your dispute.
                          The reinvestigation process can take up to 30 days.
                        </Trans>
                      </p>
                      <p>
                        <Trans>
                          By providing the above information, you confirm is
                          accurate and complete. By clicking "Submit" you agree
                          to be contacted by Certn for additional details, and
                          you consent to Certn collecting and disclosing your
                          personal information as per our Privacy Policy to
                          resolve the dispute on your report.
                        </Trans>
                      </p>
                      <p>
                        <Trans>
                          Contact us at{" "}
                          <Link
                            textStyle="global/body-secondary"
                            textColor="text-weak"
                            target="_blank"
                            href="mailto: compliance@certn.co"
                            testID="dispute-report-link-compliance-email"
                          >
                            compliance@certn.co{" "}
                          </Link>
                          or at +1 (844) 987 0690 for additional assistance or
                          information.
                        </Trans>
                      </p>
                    </Text>
                  </GridItem>
                </Grid>
              </FormModal>
            );
          }}
        </Formik>
      )}
    </ActionListItemWithModal>
  );
}
